import { RefObject, useEffect } from "react";
import { useMessageStore } from "../store";
export enum ScrollAction {
  FORCE_TO_BOTTOM = "forceToBottom",
  AUTO = "auto",
}

export enum ScrollFrom {
  FILE_LOADED,
  LOAD_HISTORY,
  SEND_MSG,
  SATISFY,
  QUESTION_LIST,
  NEW_MESSAGE,
  LINE_UP,
  INIT_SUCCESS,
}

interface Options {
  actionType?: ScrollAction;
  pageVisible?: boolean;
  from?: ScrollFrom;
}
export const useScrollToBottom = (
  containerRef: RefObject<HTMLDivElement | null>,
) => {
  const setBottomReachedStatus = useMessageStore(
    (state) => state.setBottomReachedStatus,
  );
  const resetUnreadCount = useMessageStore((state) => state.resetUnreadCount);

  const scrollToBottom = (
    containerRef: RefObject<HTMLElement | null> | null | undefined,
    options: ScrollToOptions & Options = {},
  ): void => {
    const actionType = options.actionType || ScrollAction.AUTO;
    const pageVisible = options.pageVisible;
    const behavior = options.behavior || "smooth";
    const { bottomReached } = useMessageStore.getState();
    try {
      // 获取实际滚动容器（兼容 null 情况）
      const container = containerRef?.current || document.documentElement;

      // 当actionType为CLICK时，直接触发滚动
      if (
        actionType === ScrollAction.AUTO &&
        (!bottomReached || pageVisible === false)
      ) {
        // 如果没有在底部或者页面隐藏了，即使有来源消息，也不滚动到底部，在不触发滚动到底部逻辑
        return;
      }

      // 只有确认容器存在时才执行滚动
      if (container && "scrollTo" in container) {
        // 在 setTimeout 内部获取 scrollHeight，确保获取到最新的高度
        setTimeout(() => {
          const scrollHeight = container?.scrollHeight || 0;
          container.scrollTo({
            top: scrollHeight,
            behavior,
          });
        }, 100);
      }
    } catch (error) {
      console.error("滚动失败:", error);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const container = containerRef?.current;
      if (!container) return;
      const { bottomReached } = useMessageStore.getState();
      // 计算是否滚动到底部
      const isBottomReached =
        container.scrollHeight - container.scrollTop <=
        // 当滚动条向上滚动距离大于100，则来了新消息不会滚动
        container.clientHeight + 100;
      if (bottomReached !== isBottomReached) {
        setBottomReachedStatus(isBottomReached);
        if (isBottomReached) {
          resetUnreadCount();
        }
      }
    };
    const container = containerRef?.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    // 初始检查一次
    handleScroll();
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef.current]);

  return {
    scrollToBottom,
  };
};
